import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';

import {showModal} from '@edna/components';
import {Modal, ModalActions, ModalCancelButton, ModalSubmitButton} from '@edna/components/Modals';
import {Stack} from '@edna/components/primitives';
import {ECurrencySymbol, TCurrency} from '@edna/utils';

import {Message} from 'src/components';
import {SecondaryText} from 'src/components/primitives';
import {useWebNotifications} from 'src/containers/Notifications';
import {useGetTenantDataQuery} from 'src/containers/User';
import {twoDigitsAfterPoint} from 'src/utils';
import {joiResolver} from 'src/utils/validator';

import {useCreateStripeInvoiceMutation} from '../api';
import * as S from '../style';

const MAX_AMOUNT_LENGTH = 14;
const MIN_INVOICE_AMOUNT = 5;

type TProps = {
  closeModal: TEmptyFunction;
};

const resolver = (currency?: TCurrency) =>
  joiResolver((joi) =>
    joi.object({
      amount: joi
        .number()
        .required()
        .custom((value, helpers) => {
          if (value && !twoDigitsAfterPoint.test(value)) {
            return helpers.error('invalidCurrency');
          }

          if (value < MIN_INVOICE_AMOUNT) {
            return helpers.error('minCurrency', {
              currency: currency ?? '',
              minAmount: MIN_INVOICE_AMOUNT,
            });
          }

          return value;
        }),
    }),
  );

const StripeSettingsModal = React.memo<TProps>(({closeModal}) => {
  const {t} = useTranslation();

  const invoiceNotifications = useWebNotifications({type: 'InvoicePaid'});

  const [createInvoice, {data: createdInvoice, isLoading: isCreating}] =
    useCreateStripeInvoiceMutation();
  const {data: tenantData} = useGetTenantDataQuery();

  const currency = tenantData?.defaultCurrency;
  const isWaiting = isCreating || !!createdInvoice;

  const stripeValidate = React.useMemo(() => resolver(currency), [currency]);

  React.useEffect(() => {
    if (isWaiting && !!invoiceNotifications.length) {
      closeModal();
    }
  }, [invoiceNotifications, isWaiting, closeModal]);

  const methods = useForm({
    mode: 'all',
    defaultValues: {paymentBankId: 2},
    resolver: stripeValidate,
  });

  const onSubmit = React.useCallback(
    async ({amount}) => {
      try {
        const invoice = await createInvoice({amount, currency}).unwrap();

        if (invoice?.paymentUrl) {
          window.open(invoice.paymentUrl, '_blank', 'noreferrer');
        }
      } catch {}
    },
    [currency, createInvoice],
  );

  return (
    <Modal width="530px">
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <S.StripeModalHeader>
            <Trans i18nKey="BalanceReplenishment:stripeSettingsModal.title">
              <S.StripeLogoIcon />
            </Trans>
          </S.StripeModalHeader>
          <SecondaryText size="S">
            {t('BalanceReplenishment:stripeSettingsModal.amount')}
          </SecondaryText>
          <Stack align="baseline" direction="ROW" size="2">
            <S.NumberInputField name="amount" isDecimal maxLength={MAX_AMOUNT_LENGTH} />
            {currency && <S.Currency>{ECurrencySymbol[currency]}</S.Currency>}
          </Stack>
          <Message
            type="Warning"
            shadowSize="XS"
            title={t('BalanceReplenishment:stripeSettingsModal.warning')}
          />
          <SecondaryText size="S">
            {t('BalanceReplenishment:stripeSettingsModal.content')}
          </SecondaryText>
          <ModalActions>
            <ModalCancelButton data-selector="ModalCancelButton" onClick={closeModal}>
              {t('BalanceReplenishment:stripeSettingsModal.cancel')}
            </ModalCancelButton>
            <ModalSubmitButton
              loading={isWaiting}
              disabled={!methods.formState.isValid || isWaiting}
            >
              {t('BalanceReplenishment:stripeSettingsModal.topUp')}
            </ModalSubmitButton>
          </ModalActions>
        </form>
      </FormProvider>
    </Modal>
  );
});

StripeSettingsModal.displayName = 'StripeSettingsModal';

const showStripeOnetimePaymentSettingsModal = () =>
  showModal((closeModal) => <StripeSettingsModal closeModal={closeModal} />);

export {showStripeOnetimePaymentSettingsModal};
